import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ClipboardList, Droplets, FileText, HeartPulse, Menu, Pill, Users, X } from 'lucide-react';
import { ROUTES } from '../constants/routes';
import { useSession } from '../contexts/SessionContext';
import { useLanguage } from '../contexts/LanguageContext';

const TAB_KEYS = [
  { path: ROUTES.PATIENTS, labelKey: 'patients', icon: Users },
  { path: ROUTES.NOTES, labelKey: 'notes', icon: ClipboardList },
  { path: ROUTES.VITALS, labelKey: 'vitals', icon: HeartPulse },
  { path: ROUTES.MEDICATIONS, labelKey: 'medications', icon: Pill },
  { path: ROUTES.FLUID_BALANCE, labelKey: 'fluidBalance', icon: Droplets },
  { path: ROUTES.REPORTS, labelKey: 'reports', icon: FileText },
];

const HIDE_NAV_PREFIXES = [
  '/login',
  '/profile',
  '/onboarding',
  '/select-patient',
  '/shift-handover',
  '/free-record',
  '/record',
  '/generating',
];

export default function BottomNav({ recordingWarning }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isRecordingActive } = useSession();
  const { t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);

  const hideNav =
    HIDE_NAV_PREFIXES.some((p) => location.pathname.startsWith(p)) ||
    location.pathname.includes('/record') ||
    location.pathname.includes('/generating') ||
    location.pathname.includes('/review') ||
    location.pathname.includes('/pre-record') ||
    location.pathname.includes('/admission');

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [menuOpen]);

  if (hideNav) return null;

  const handleTabClick = (path) => {
    if (isRecordingActive) return;
    setMenuOpen(false);
    navigate(path);
  };

  return (
    <>
      {(recordingWarning || isRecordingActive) && (
        <div className="fixed bottom-16 left-0 right-0 z-50 px-4 pb-safe">
          <div className="mx-auto max-w-lg rounded-xl bg-warning px-4 py-3 text-center text-sm font-medium text-white shadow-lg">
            {t('recordingWarning')}
          </div>
        </div>
      )}

      <nav className="fixed bottom-0 left-0 right-0 z-50 w-full border-t border-gray-200 bg-surface pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
        <div className="flex min-h-nav items-center px-4 py-2">
          <button
            type="button"
            onClick={() => {
              if (isRecordingActive) return;
              setMenuOpen(true);
            }}
            className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-200"
            aria-label={t('menu')}
          >
            <Menu className="h-5 w-5" strokeWidth={2} />
            {t('menu')}
          </button>
        </div>
      </nav>

      {menuOpen ? (
        <div className="fixed inset-0 z-[200] flex flex-col bg-white">
          <div className="flex items-center justify-between px-5 pb-2 pt-[max(1rem,env(safe-area-inset-top))]">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-gray-400">
              {t('switchTo')}
            </p>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              className="rounded-full bg-gray-100 p-2 text-gray-600"
              aria-label={t('close')}
            >
              <X className="h-5 w-5" strokeWidth={2} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-2">
            <div className="mx-auto flex w-full max-w-md flex-col gap-3">
              {TAB_KEYS.map(({ path, labelKey, icon: Icon }) => {
                const isActive =
                  location.pathname === path
                  || (path === ROUTES.FLUID_BALANCE && location.pathname.includes('/fluid-balance'))
                  || (path !== ROUTES.PATIENTS
                    && path !== ROUTES.FLUID_BALANCE
                    && location.pathname.startsWith(path));

                return (
                  <NavLink
                    key={path}
                    to={path}
                    onClick={(e) => {
                      e.preventDefault();
                      handleTabClick(path);
                    }}
                    className={`flex items-center gap-3 rounded-full bg-white px-4 py-3.5 text-left shadow-[0_2px_10px_rgba(15,23,42,0.08)] ring-1 transition-colors ${
                      isActive
                        ? 'text-navy ring-navy/20'
                        : 'text-gray-700 ring-gray-100 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="h-5 w-5 shrink-0" strokeWidth={2} />
                    <span className="text-[15px] font-semibold">{t(labelKey)}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
